import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import AuthShell from "./AuthShell";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const next = new URLSearchParams(window.location.search).get("next");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const u = await register({ name: form.name, email: form.email, password: form.password, remember });
      toast.success("Account created — welcome!");
      navigate(next || (u.role === "customer" ? "/book" : "/dashboard"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-0 transition-colors duration-200";
  const eyeBtn = "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#D4AF37] transition-colors duration-200";

  return (
    <AuthShell testid="register-page" title="Create Account" subtitle="Create a quick account to book your appointment.">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="register-name" required placeholder="Full name" value={form.name} onChange={set("name")} className={field} />
        <input data-testid="register-email" type="email" required placeholder="Email address" value={form.email} onChange={set("email")} className={field} />
        <div className="relative">
          <input data-testid="register-password" type={showPw ? "text" : "password"} required minLength={6} placeholder="Create password (min 6 chars)" value={form.password} onChange={set("password")} className={field + " pr-12"} />
          <button type="button" data-testid="register-password-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className={eyeBtn}>
            {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <div className="relative">
          <input data-testid="register-confirm" type={showConfirm ? "text" : "password"} required placeholder="Confirm password" value={form.confirm} onChange={set("confirm")} className={field + " pr-12"} />
          <button type="button" data-testid="register-confirm-toggle" onClick={() => setShowConfirm((v) => !v)} aria-label={showConfirm ? "Hide password" : "Show password"} className={eyeBtn}>
            {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer"><input type="checkbox" data-testid="register-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#D4AF37]" /> Remember me</label>
        <button data-testid="register-submit" disabled={loading} className="w-full py-3.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account & Continue"}
        </button>
      </form>
      <p className="text-gray-400 text-sm mt-6 text-center">
        Already have an account? <Link to={`/login${window.location.search}`} className="text-[#D4AF37]">Sign in</Link>
      </p>
    </AuthShell>
  );
}
